/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2018 ICEMEDX, Inc.
***********************************************/
import { AfterViewInit, ChangeDetectorRef, Inject, Renderer2, ViewChild } from '@angular/core';
import { Component, OnInit, Input, Injectable, EventEmitter, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { IcecontrolSettings, IcecontrolPermission, iceColumnDefinition } from '../models/icesettings';
import { Iceutility } from '../../services/iceutility';
import { IcematgridComponent } from '../icepanel/icematgrid.component';
import { userTypes } from '../../login/login.config';
import { IceMultimediaCanvas } from './IceMultimediaCanvas';
import { IceMultimediaService } from '../../services/multimedia/icemultimedia.service';
import { IceMultimediaPage, multimediaModes } from './IceMultimediaPage';
import { IMultimediaData } from './IMultimediaData';
import { IceMultimediaStroke } from './IceMultimediaStroke';
import { IcepanelComponent } from '../icepanel/icepanel.component';
import { ITabProperties } from '../icetab/ITabProperties';

@Component({
  selector: 'icemultimedia',
  templateUrl: './iceMultimedia.component.html',
  styleUrls: ['./iceMultimedia.component.scss'],
  providers: [IcematgridComponent, IceMultimediaService]
})
@Injectable()

export class IceMultimediaComponent implements OnInit, AfterViewInit {
  // Input Members
  @Input() id: string = "";
  @Input() properties: any = {};
  @Input() ctrl: IcecontrolSettings = null;
  @Input() tableColumn: iceColumnDefinition = null;
  @Input() tableRowData: any = null;
  @Input() matTableObject: IcematgridComponent = null;
  @Input() activeTabIndex: number = 0;
  @Output() OnPageAdd: EventEmitter<any> = new EventEmitter<any>();
  @Output() OnPageRemove: EventEmitter<any> = new EventEmitter<any>();
  @Output() OnPageDraw: EventEmitter<any> = new EventEmitter<any>();

  // Data Members
  iceFormControl: FormControl = null;
  permission: IcecontrolPermission = null;
  inputType: string = "text";
  _settings: any = {};
  pages: IceMultimediaPage[] = [];
  control_id = "";
  controls = {};
  activePage = null;
  width = 100;
  height = 100;
  key = "";
  fillcolor: string = "";
  linewidth: number;
  canvasObj: any;
  tabCtrl: IcecontrolSettings = null;
  firstTab: ITabProperties;

  // private fields
  private c_tag = null;
  private value = null;

  @ViewChild("multimediaParent")
  private gParent;

  constructor(private renderer2: Renderer2, private cdr: ChangeDetectorRef, private multimediaSvc: IceMultimediaService,
    @Inject(IcepanelComponent) private parent: IcepanelComponent,
    @Inject(IcematgridComponent) private parentTable: IcematgridComponent) {
  }

  ngOnInit() {
    this.key = Iceutility.guid();
    this._settings = this.ctrl.properties;
    this.width = Iceutility.isMobileDevice() ? window.outerWidth : this.ctrl.properties.width;
    this.height = this.ctrl.properties.height;
    this.permission = this.ctrl.permissions ? this.ctrl.permissions : new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaNone);

    if (this.parentTable && this.tableRowData != null && this.tableColumn != null) {
      // tables should not use the standard formcontrol
      let formRowGroup: FormGroup = this.parentTable.getFormGroup(this.tableRowData);
      this.iceFormControl = new FormControl(this.tableRowData[this.tableColumn.columnName]);
      formRowGroup.addControl(this.tableColumn.columnName, this.iceFormControl);
    }
    else {
      // stardard formcontrol processing
      this.iceFormControl = this.ctrl.getFormControl();
      if (this.iceFormControl == null)
        this.iceFormControl = new FormControl(false); // No control so we create a placeholder control to prevent an errors
    }

    if (this.permission.default_access === userTypes.USER_FEATURE_ACCESS.ufaReadOnly) {
      this.iceFormControl.disable();
    }

    if (this._settings.showControlButtons) {
      this.firstTab = this.getNewTab();
      this.tabCtrl = new IcecontrolSettings("icetab", { width: this.width, height: this.height, tabs: <ITabProperties[]>[this.firstTab] }, null, null, "tabs", "")
        .createFormControl(0, []);
    }

    this.multimediaSvc.multimediaDataSub.subscribe(data => {
      this.SetControlValue();
    });
  }

  ngAfterViewInit() {
    if (!this._settings.showControlButtons) {
      this.create(this.gParent.nativeElement, this.ctrl.tag, this.key, this.id, this.ctrl.properties, this.ctrl.jsEvents);
    } else {
      let tabContentDiv = document.createElement("div");
      this.create(tabContentDiv, this.ctrl.tag, this.key, this.id, this.ctrl.properties, this.ctrl.jsEvents);
      let tab = document.getElementById(this.firstTab.id);
      tab.appendChild(tabContentDiv);
    }
  }

  create(parent, tag, key, id, settings, events) {
    if (parent == null)
      return false;

    if (tag == null)
      return false;

    if (key == null || key.length == 0)
      key = Iceutility.guid();

    try {
      this.key = key;
      this.control_id = id;
      this._settings = settings;
      if (Iceutility.isdefined(this._settings["pagesettings"])) {
        this._settings["pagesettings"]["disabled"] = this._settings.disabled;
      }
      this.value = this._settings.value;
      this.c_tag = tag;

      // Create new canvas for tab
      var cnvsobj: IceMultimediaCanvas = new IceMultimediaCanvas(this.ctrl, this.renderer2, this.multimediaSvc);
      this.canvasObj = cnvsobj;
      var elem = cnvsobj.create(parent, this.c_tag);
      this.controls[cnvsobj.control_id] = cnvsobj;
      this.activePage = cnvsobj.control_id;
      this.pages.push(this.canvasObj.page);

      if (!this._settings.disabled) {
        elem.style.border = "1px solid #e0e0e0";
      }
      parent.appendChild(elem);

      if (this._settings["showControlButtons"] && !this._settings.disabled) {
        var container = document.createElement("div");
        container.setAttribute("id", "container_" + this.control_id);
        container.setAttribute("iField", this.c_tag);
        container.style.paddingLeft = "1px";
        container.style.height = "40px";
        parent.appendChild(container);
        if (settings.visible == false) {
          container.hidden = true;
        }

        // Footer div
        var footer = document.createElement("div");
        footer.setAttribute("id", "footer_tab_" + this.control_id);
        container.appendChild(footer);

        /////////////// Footer contents

        // draw button
        let pencilBtn = document.createElement("button"),
          iconContainer = document.createElement("mat-icon");
        pencilBtn.title = "Draw";
        pencilBtn.className = "mat-raised-button";
        iconContainer.className = "fa fa-paint-brush";
        pencilBtn.appendChild(iconContainer);

        pencilBtn.addEventListener("click", () => {
          this.canvasObj.page.defaultMode = multimediaModes.modeDraw;
          document.getElementById(this.activePage).style.cursor = "pointer";
        }, false);
        footer.appendChild(pencilBtn);

        // font button
        let textBtn = document.createElement("button"),
          textIconContainer = document.createElement("mat-icon");
        textBtn.title = "Insert Text";
        textBtn.className = "mat-raised-button";
        textIconContainer.className = "fa fa-font";
        textBtn.appendChild(textIconContainer);

        textBtn.addEventListener("click", () => {
          this.canvasObj.page.defaultMode = multimediaModes.modeText;
          document.getElementById(this.activePage).style.cursor = "text";
        }, false);
        footer.appendChild(textBtn);

        // erase button

        let eraseBtn = document.createElement("button"),
          eraseIconContainer = document.createElement("mat-icon");
        eraseBtn.title = "Eraser";
        eraseBtn.className = "mat-raised-button";
        eraseIconContainer.className = "fa fa-eraser";
        eraseBtn.appendChild(eraseIconContainer);

        eraseBtn.addEventListener("click", () => {
          this.canvasObj.page.defaultMode = multimediaModes.modeErase;
          document.getElementById(this.activePage).style.cursor = 'url("https://img.icons8.com/material/24/000000/eraser--v1.png"), auto';
        }, false);
        footer.appendChild(eraseBtn);

        if (this._settings.showControlButtons) {
          // add page button
          var addPageBtn = document.createElement("button");
          addPageBtn.innerText = "Add Page";
          addPageBtn.setAttribute("id", "btnAdd");
          addPageBtn.className = "mat-raised-button";

          addPageBtn.addEventListener("click", () => {
            this.AddNewTab();
          }, false);
          footer.appendChild(addPageBtn);

          // remove page button
          var removePageBtn = document.createElement("button");
          removePageBtn.innerText = "Remove Page";
          removePageBtn.setAttribute("id", "btnRemove");
          removePageBtn.className = "mat-raised-button";

          removePageBtn.addEventListener("click", () => {
            this.RemoveTab();
          }, false);
          footer.appendChild(removePageBtn);
        }

        if (Iceutility.isdefined(elem) == false) {
          throw { message: "Unable to initialize canvas." };
        }
      }

      this.HideTextEditBox();
      this.DrawMultimediaData();
    }
    catch (e) {
      console.error(e.message);
    }
  }

  DrawMultimediaData() {
    let pages: IceMultimediaPage[] = this.multimediaSvc.deserialize(this.iceFormControl.value);
    let canvas: IceMultimediaCanvas;

    for (let iPg = 0; iPg < pages.length; iPg++) {
      let page = pages[iPg];
      canvas = iPg > 0 ? new IceMultimediaCanvas(this.ctrl, this.renderer2, this.multimediaSvc) : this.canvasObj;
      if (this._settings.showControlButtons == true) this.AddNewTab();

      // draw strokes
      for (let iStroke = 0; iStroke < page.strokeList.length; iStroke++) {
        let stroke = page.strokeList[iStroke];
        canvas.currentStroke = new IceMultimediaStroke(null, stroke.point);

        for (let iPoint = 0; iPoint < stroke.pointArray.length; iPoint++) {
          let point = stroke.pointArray[iPoint];
          canvas.prevPoint = iPoint === 0 ? point : stroke.pointArray[iPoint - 1];
          page.draw(canvas, point.x, point.y);
        }
      }

      // draw texts
      for (let iText = 0; iText < page.textList.length; iText++) {
        let text = page.textList[iText];
        page.draw(canvas, text.point.x, text.point.y, text.data);
      }
    }
  }

  SetControlValue() {
    let value = "version=3;pg_count=" + this.pages.length + ";currentpage=" + this.activeTabIndex.toString() + ";pg_datetime=" + new Date().toISOString() + ";pg_xpos=0;pg_ypos=0;pg_layout=NN;%;";

    for (let iPage = 0; iPage < this.pages.length; iPage++) {
      value += this.pages[iPage].pageData;
    }

    let imgSrc = this.GetCanvasImage(),
      multimediaData: IMultimediaData = { value: value, multimediaImage: imgSrc };

    this.iceFormControl.setValue(multimediaData);
  }

  DrawBitmapDetails(imageurl, posX, posY, imgWidth, imgHeight) {
    let data = this.controls[this.activePage],
      outlineImage = new Image();
    outlineImage.src = imageurl;
    data.context.drawImage(outlineImage, posX || 0, posY || 0, imgWidth || (outlineImage.width / 2), imgHeight || (outlineImage.height / 2) - 120);
  }

  Destroy() {
    document.getElementById(this.control_id).remove();
  }

  AddNewTab() {
    try {
      this.generateNewTab();
    } catch (e) {
      console.log(e.message);
    }
  }

  RemoveTab() {
    try {
      if (this.tabCtrl.properties.tabs.length === 1) {
        return;
      }

      this.tabCtrl.properties.tabs.splice(this.activeTabIndex - 1, 1);
      this.pages.splice(this.activeTabIndex - 1, 1);
      this.SetControlValue();

      for (let iTab = 0; iTab < this.tabCtrl.properties.tabs.length; iTab++) {
        let tab: ITabProperties = this.tabCtrl.properties.tabs[iTab];
        tab.label = "Page " + (iTab + 1);
      }
      this.activeTabIndex--;
    } catch (e) {
      console.log(e.message);
    }
  }

  SetTextareaValue(value) {
    if (Iceutility.isdefined(value) == false) {
      return;
    }

    try {
      let button = document.getElementById("btnDone");

      if (Iceutility.isdefined(button) == false) { // Edit mode is not enabled
        let c_obj = this.controls[this.activePage];

        if (Iceutility.isdefined(c_obj) == false) {
          throw { message: "Unable to get current context." };
        }
        c_obj.page.defaultMode = 3;

        // Added to fix issue where a page gets added
        // Add x and y co-ordinates
        c_obj.page.m_strokeList.push({ x: "10", y: "20", z: 0 });
        let canvas_obj = document.getElementById(c_obj.control_id);
        canvas_obj.click();
      }

      setTimeout(() => {
        button = document.getElementById("btnDone");

        if (Iceutility.isdefined(button)) {
          let area: any = document.getElementById("btnDone").parentElement.children[2],
            temp_val = area.value;

          if (button.parentElement.hidden) {
            temp_val = "";
            button.parentElement.hidden = false;
            button.parentElement.removeAttribute("class");
          }

          area.value = temp_val + " " + value;
        }
      }, 100);
    } catch (e) {
      console.log(e.message);
    }
  }

  GetCanvasImage() {
    if (!Iceutility.isdefined(this.canvasObj)) {
      return "";
    }

    return this.canvasObj.context.canvas.toDataURL();
  }

  destroy() {
    document.getElementById(this.control_id).remove();
  }

  HideTextEditBox() {
    try {
      setInterval(function () {
        let button = document.getElementById("btnDone");

        if (Iceutility.isdefined(button)) {
          let parent = document.getElementById("btnDone").parentElement.children[2].getAttribute("data-parent"),
            elem = document.getElementById(parent);

          if (elem == null || elem.hidden == true) {
            button.parentElement.remove();
          }
        }
      }, 500);
    } catch (e) {
    }
  }

  GetCurrentPage() {
    return this.controls[this.activePage].page;
  }

  onTabSelectionChange(index) {
    this.activeTabIndex = index;
  }

  private generateNewTab() {
    if (this.tabCtrl == null) return;
    let newPageNumber = this.pages.length + 1,
      newTab = this.getNewTab(newPageNumber);
    this.tabCtrl.properties.tabs.push(newTab);

    let tabContentDiv = document.createElement("div");
    this.create(tabContentDiv, this.ctrl.tag, this.key, this.id, this.ctrl.properties, this.ctrl.jsEvents);
    this.activeTabIndex++;

    setTimeout(() => {
      let tab = document.getElementById(newTab.id);
      tab.appendChild(tabContentDiv);
    }, 500);
  }

  private getNewTab(pageNumber: number = 1): ITabProperties {
    return { label: ("Page " + pageNumber), content: "", id: ("tab_" + Iceutility.guid()), isHtmlContent: true }
  }
}
